<template>
<div class="pl-cl">

    <!-- toolbar: search + counts + expand-all -->
    <div class="pl-cl-bar">
        <input type="search" class="pl-cl-search" v-model="search"
               placeholder="🔍 Search jobs — name, company or Q number"
               aria-label="Search jobs in flight">
        <span class="pl-cl-count">
            <b>{{ filteredJobs.length }}</b> job<template v-if="filteredJobs.length !== 1">s</template> in flight
            <template v-if="filtering && filteredJobs.length !== jobs.length"> (of {{ jobs.length }})</template>
        </span>
        <!-- P4 flag filters. The planner toolbar's own pills are hidden on
             this zoom, so the checklist carries its own pair. -->
        <button type="button" class="pl-cl-flagbtn" :class="{ on: flagArtwork }"
                title="Only jobs waiting on artwork" @click="flagArtwork = !flagArtwork">⏳ artwork</button>
        <button type="button" class="pl-cl-flagbtn" :class="{ on: flagPriority }"
                title="Only priority jobs" @click="flagPriority = !flagPriority">★ priority</button>
        <!-- expand-all renders every process into the DOM, so the browser's
             own Ctrl+F works across the whole shop — the one genuine virtue
             of the old flat checklist page -->
        <button type="button" class="pl-cl-expand" @click="toggleAll">
            {{ allOpen ? '▾ Collapse all' : '▸ Expand all' }}
        </button>
    </div>

    <div v-if="!jobs.length" class="pl-cl-empty">
        Nothing in flight — jobs appear here once they have production work outstanding.
    </div>
    <div v-else-if="!filteredJobs.length" class="pl-cl-empty">
        <template v-if="search">No jobs match “{{ search }}”<template v-if="flagArtwork || flagPriority"> with that flag</template> — try part of the client name, company or the Q number.</template>
        <template v-else>No jobs with that flag right now.</template>
    </div>

    <!-- one row per job in flight, deadline order (soonest first) -->
    <div v-for="job in filteredJobs" :key="'cl' + job.quote_id" class="pl-cl-job">
        <button type="button" class="pl-cl-head" @click="toggle(job.quote_id)">
            <span class="pl-cl-chev">{{ expanded[job.quote_id] ? '▾' : '▸' }}</span>
            <span class="pl-cl-id">Q{{ job.quote_id }}</span>
            <span class="pl-cl-nm">
                {{ job.quote_name }}
                <small v-if="job.company"> — {{ job.company }}</small>
            </span>
            <!-- P4 flags — the pre-production pill is left off: the row's
                 "not yet planned" ghost + the panel's unreleased rows say it -->
            <flag-pills class="pl-cl-flags" :flags="job.flags" :show-pre="false" />

            <span v-if="job.unplanned" class="pl-cl-pill pl-cl-pill-ghost" title="Nothing placed on the planner yet — dates are from its CMS card">not yet planned</span>

            <span class="pl-cl-right">
                <span v-if="job.deadline" class="pl-cl-pill" :class="'pl-cl-risk-' + job.risk"
                      :title="job.risk === 'late' ? 'Deadline missed or work runs past it' : (job.risk === 'tight' ? 'Work finishes within 2 days of the deadline' : 'On track')">
                    ⚑ {{ fmtDay(job.deadline) }}
                </span>
                <span class="pl-cl-prog" :title="job.pct_complete + '% of processes done'">
                    <span class="pl-cl-prog-in" :style="{ width: job.pct_complete + '%' }"></span>
                </span>
                <span class="pl-cl-pct">{{ job.pct_complete }}%</span>
                <!-- £ only reaches planner/sales payloads — the board strips
                     value_pence for the shop-floor tier -->
                <span v-if="job.value_pence != null" class="pl-cl-val">£{{ fmtMoney(job.value_pence) }}</span>
            </span>
        </button>

        <job-process-panel v-if="expanded[job.quote_id]"
                           class="pl-cl-panel"
                           :quote-id="job.quote_id" :board="board" :permissions="permissions"
                           :refresh-key="refreshKey" :tickable="true" :full-height="true"
                           @open-chip="$emit('open-chip', $event)"
                           @changed="$emit('refetch')"
                           @error="$emit('error', $event)" />
    </div>
</div>
</template>

<script>
import JobProcessPanel from './JobProcessPanel';
import FlagPills from './FlagPills';

export default {
    name: 'ChecklistView',
    components: { JobProcessPanel, FlagPills },

    props: {
        board: { type: Object, required: true },
        permissions: { type: Object, required: true },
        refreshKey: { type: Number, default: 0 },
    },

    data() {
        return {
            search: '',
            expanded: {},   // quote_id -> bool
            flagArtwork: false,    // P4: only jobs waiting on artwork
            flagPriority: false,   // P4: only ★ jobs
        };
    },

    computed: {
        // Server-sorted by deadline (soonest first, undated last) — keep it.
        jobs() {
            return this.board.jobs || [];
        },

        filtering() {
            return !!this.search.trim() || this.flagArtwork || this.flagPriority;
        },

        /* Search AND the flag toggles — both narrow. The job's flags decide;
           absence never matches (same rule as PlannerPage.matchesFlagFilter). */
        filteredJobs() {
            const needle = this.search.trim().toLowerCase();
            const qNeedle = needle.replace(/^q/, '');
            return this.jobs.filter(j => {
                const f = j.flags || {};
                if (this.flagArtwork && f.artwork !== 'waiting') return false;
                if (this.flagPriority && !f.priority) return false;
                if (!needle) return true;
                return String(j.quote_id).includes(qNeedle)
                    || (j.quote_name || '').toLowerCase().includes(needle)
                    || (j.company || '').toLowerCase().includes(needle);
            });
        },

        allOpen() {
            return this.filteredJobs.length > 0
                && this.filteredJobs.every(j => this.expanded[j.quote_id]);
        },
    },

    methods: {
        toggle(quoteId) {
            this.$set(this.expanded, quoteId, !this.expanded[quoteId]);
        },

        toggleAll() {
            const open = !this.allOpen;
            this.filteredJobs.forEach(j => this.$set(this.expanded, j.quote_id, open));
        },

        fmtDay(date) {
            return new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        },

        fmtMoney(pence) {
            return Math.round(pence / 100).toLocaleString('en-GB');
        },
    },
};
</script>

<style scoped>
.pl-cl{padding:0 2px}
.pl-cl-bar{display:flex; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:12px}
/* PlannerPage's .pl-filter is scoped to that component, so the look is
   restated here rather than borrowed */
.pl-cl-search{min-width:280px; flex:0 1 340px; border:1px solid var(--pl-line);
    background:var(--pl-card); border-radius:8px; padding:8px 10px;
    color:var(--pl-ink); font-weight:600; font-size:14px}
.pl-cl-count{font-size:13px; color:var(--pl-ink2); font-weight:600}
/* P4 flag toggles — same look as PlannerPage's .pl-flagbtn (scoped there) */
.pl-cl-flagbtn{border:1px solid var(--pl-line); background:var(--pl-card); border-radius:20px; padding:6px 11px;
    font-weight:700; font-size:12.5px; color:var(--pl-ink2); white-space:nowrap; cursor:pointer}
.pl-cl-flagbtn:hover{border-color:var(--pl-ink3)}
.pl-cl-flagbtn.on{background:var(--pl-ink); border-color:var(--pl-ink); color:#fff}
.pl-cl-expand{margin-left:auto; border:1px solid var(--pl-line); background:var(--pl-card);
    border-radius:8px; padding:5px 12px; font-weight:700; font-size:12.5px; color:var(--pl-ink)}
.pl-cl-expand:hover{border-color:var(--pl-ink3)}
.pl-cl-empty{font-size:13.5px; color:var(--pl-ink2); padding:26px 8px; text-align:center}

.pl-cl-job{margin-bottom:8px}
.pl-cl-head{display:flex; align-items:center; gap:10px; width:100%; text-align:left;
    background:var(--pl-card); border:1px solid var(--pl-line); border-radius:12px;
    padding:10px 14px; box-shadow:0 1px 2px rgba(11,11,11,.05); cursor:pointer}
.pl-cl-head:hover{border-color:var(--pl-ink3)}
.pl-cl-chev{flex:none; color:var(--pl-ink2); font-size:12px; width:14px}
.pl-cl-id{flex:none; font-weight:800; font-size:13.5px; color:var(--pl-ink)}
.pl-cl-nm{font-weight:700; font-size:13.5px; color:var(--pl-ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
.pl-cl-nm small{color:var(--pl-ink2); font-weight:650}
.pl-cl-flags{flex:none}
.pl-cl-right{display:flex; align-items:center; gap:10px; flex:none; margin-left:auto}

.pl-cl-pill{font-size:11px; font-weight:800; border-radius:20px; padding:2px 9px; white-space:nowrap}
.pl-cl-risk-ok{background:var(--pl-good-soft, #e2f6e2); color:var(--pl-good, #0ca30c)}
.pl-cl-risk-tight{background:var(--pl-warn-soft, #fdf3d9); color:var(--pl-warn, #a86a00)}
.pl-cl-risk-late{background:#f7e4e4; color:#a94442}
.pl-cl-pill-ghost{background:transparent; border:1px dashed var(--pl-ink3); color:var(--pl-ink2)}

.pl-cl-prog{width:90px; height:7px; border-radius:6px; background:var(--pl-card2); overflow:hidden; flex:none}
.pl-cl-prog-in{display:block; height:100%; background:var(--pl-good, #0ca30c); border-radius:6px}
.pl-cl-pct{font-size:12px; font-weight:800; color:var(--pl-ink2); width:38px; text-align:right; flex:none}
.pl-cl-val{font-size:12.5px; font-weight:800; color:var(--pl-ink); flex:none; min-width:64px; text-align:right}

/* the expanded panel reads as belonging to its job row */
.pl-cl-panel{margin:4px 0 10px 24px}

@media (max-width: 700px){
    .pl-cl-prog{display:none}
    .pl-cl-panel{margin-left:6px}
}
</style>
